import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import ErrorMessage from '@/components/common/ErrorMessage';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface FormProp {
  message: string;
  userType: 'employee' | 'customer' | 'staff' | 'all';
  payload: {
    title: string;
  };
  channel: 'push';
}

const SendNotification = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormProp>({
    defaultValues: {
      message: '',
      userType: 'customer',
      payload: {
        title: 'Nass Foods',
      },
      channel: 'push',
    },
  });

  const breadcrumb = [
    { label: 'Notifications', href: RoutesLink.notifications },
    { label: 'Send' },
  ];

  const options = [
    { label: 'All', value: 'all' },
    { label: 'Users', value: 'customer' },
    { label: 'staff', value: 'staff' },
    { label: 'Employees', value: 'employee' },
  ];

  const onSubmit = async (data: FormProp) => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.notification}/send-bulk`;
      await callApi(PATH, { method: 'POST', data });
      showToastNotification(
        'success',
        `Notification send successfully to ${data.userType}`,
      );
      reset();
    } catch (error) {
      ThrowError(error);
    }
  };

  const message = watch('message');

  useEffect(() => {
    if (message.length < 5) {
      setError('message', { message: 'Message should be atleast 5 char long' });
    } else {
      clearErrors();
    }
  }, [message, setError, clearErrors]);

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Card>
          <div className="space-y-5">
            {/* About Category */}
            <h2 className="text-lg font-semibold mb-4">
              Send Push Notifications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Type"
                  type="select"
                  options={options}
                  placeholder="Select whom you want to send notification?"
                  {...register('userType', {
                    ...errorTypes.required('User type'),
                  })}
                />
                <ErrorMessage message={errors.userType?.message} />
              </div>
              <div>
                <FormField
                  label="Title"
                  placeholder="Enter a title"
                  {...register('payload.title', {
                    ...errorTypes.required('Title'),
                  })}
                />
                <ErrorMessage message={errors.payload?.title?.message} />
              </div>
            </div>
            <div>
              <FormField
                label="Message"
                type="textarea"
                placeholder="Start typing your message...."
                {...register('message', {
                  ...errorTypes.required('Message'),
                })}
                rows={5}
              />
              <ErrorMessage message={errors.message?.message} />
            </div>
          </div>
        </Card>
        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default SendNotification;
